import {
  DefaultExchangePeriodHours,
  ExchangePeriodHoursType,
  InstrumentCategoryEnum,
  isDefined,
  OpenOrder,
  OrderFormData,
} from '@eih-netbroker/common';
import { useCallback, useEffect, useEffectEvent, useMemo } from 'react';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { getExchangePeriodHours } from '../../../../shared/utils/useLocalStorage';
import { useNormalOrderSchema } from '../../schemas/orderSchemas';
import { useFuturesInfo } from '../../services/order/useFuturesInfo';
import { useOrderExchange2 } from '../../services/order/useOrderExchange2';
import { useOrderModalNew } from '../../services/order/useOrderModalNew';
import { OrderFormSourceComponent } from '../../types/orderTypes';
import { OrderModal2 } from './OrderModal2';

type OrderModal2WrapperProps = {
  onCancel: (shouldResetFormToDefaults: boolean) => void;
  orderId?: string;
  isWithCurrencyExchange?: boolean;

  // props for initializing order modal from order watchlist
  initOrderFormData?: OrderFormData;
  sourceComponent?: OrderFormSourceComponent;

  // props for initializing order modal from open orders widget modify button
  openOrder?: OpenOrder;
};

export const OrderModal2Wrapper = ({ onCancel, orderId, initOrderFormData, openOrder }: OrderModal2WrapperProps) => {
  const { loadFuturesInitialMargin } = useFuturesInfo();
  const { getNormalOrderSchema } = useNormalOrderSchema();
  const { selectedAccount } = useAccounts();

  const {
    orderState: orderFormData,
    capturedExchangeData,
    handleOnBack,
    handleModalOnClose,
    handle2FARetryFlow,
    updateOrderState,
    changeInstrument,
    initialOrder,
    modalOptions,
    onConfirmHandle,
    onContinue,
    priceValidationState,
    stopActivationPriceValidationState,
    validateStopActivationPrice,
  } = useOrderModalNew({
    onCancel,
    order: initOrderFormData,
    orderId,
  });

  const isValid = useMemo(() => {
    try {
      getNormalOrderSchema(orderFormData).validateSync(orderFormData, {
        abortEarly: false,
      });
      return (
        !isDefined(priceValidationState.errorMessage) &&
        !priceValidationState.isLoading &&
        !isDefined(stopActivationPriceValidationState.errorMessage) &&
        !stopActivationPriceValidationState.isLoading
      );
    } catch (e) {
      return false;
    }
  }, [orderFormData, getNormalOrderSchema, priceValidationState, stopActivationPriceValidationState]);

  const exchangePeriodHours = useMemo<ExchangePeriodHoursType>(
    () => getExchangePeriodHours() ?? DefaultExchangePeriodHours,
    [],
  );

  const handle2FARetry = useCallback(() => handle2FARetryFlow(handleOnBack), [handle2FARetryFlow, handleOnBack]);

  const exchangeData = useOrderExchange2({
    orderForm: orderFormData,
    selectedAccount,
    openOrder,
    exchangePeriodHours,
  });

  const initOrderScreen = useEffectEvent(() => {
    if (orderFormData.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures) {
      loadFuturesInitialMargin(orderFormData.instrument.ticker);
    }
  });

  useEffect(() => {
    initOrderScreen();
  }, []);

  return (
    <OrderModal2
      exchangeData={exchangeData}
      capturedExchangeData={capturedExchangeData}
      onCancel={onCancel}
      onBack={handleOnBack}
      onClose={handleModalOnClose}
      isOrderFormInvalid={!isValid}
      sourceComponent={OrderFormSourceComponent.Modal}
      orderId={orderId}
      orderState={orderFormData}
      updateOrderState={updateOrderState}
      changeInstrument={changeInstrument}
      initialOrder={initialOrder}
      modalOptions={modalOptions}
      on2FARetry={handle2FARetry}
      onConfirmHandle={onConfirmHandle}
      onContinue={onContinue}
      priceValidationState={priceValidationState}
      stopActivationPriceValidationState={stopActivationPriceValidationState}
      onStopActivationPriceRevalidate={validateStopActivationPrice}
    />
  );
};
